import React from 'react';
import { Card, CardContent } from '../UI/card';
import { Badge } from '../UI/badge';
import { Button } from '../UI/button';
import {
  FileText,
  Download,
  Mail,
  Eye,
  Calendar,
  Building,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle
} from 'lucide-react';

/**
 * COMPOSANT: HISTORIQUE DES APPELS À COTISATION
 * 
 * Liste des appels générés avec actions
 */

const CallHistory = ({ calls, onView, onDownloadExcel, onDownloadPDF, onSend }) => {
  if (!calls || calls.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Aucun appel à cotisation</h3>
          <p className="text-muted-foreground">
            Commencez par générer votre premier appel à cotisation
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getMonthName = (month) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[month - 1];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SENT':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'DRAFT':
        return <Clock className="w-4 h-4 text-gray-600" />;
      case 'PENDING':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      SENT: 'success',
      DRAFT: 'default',
      PENDING: 'warning',
      FAILED: 'destructive'
    };
    const labels = {
      SENT: 'Envoyé',
      DRAFT: 'Brouillon',
      PENDING: 'En attente',
      FAILED: 'Échec'
    };
    return { variant: variants[status] || 'default', label: labels[status] || status };
  };

  return (
    <div className="space-y-4">
      {calls.map((call) => {
        const statusBadge = getStatusBadge(call.status);
        
        return (
          <Card key={call.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                {/* Informations principales */}
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <Building className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-lg">
                      {call.enterprise?.nom}
                    </h3>
                    <Badge variant={statusBadge.variant}>
                      <span className="flex items-center space-x-1">
                        {getStatusIcon(call.status)}
                        <span>{statusBadge.label}</span>
                      </span>
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                    {/* Période */}
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {getMonthName(call.month)} {call.year}
                      </span>
                    </div>

                    {/* Nombre d'employés */}
                    <div>
                      <span className="text-muted-foreground">Employés:</span>{' '}
                      <span className="font-semibold">{call.totalEmployees}</span>
                    </div>

                    {/* Montant total */}
                    <div>
                      <span className="text-muted-foreground">Montant:</span>{' '}
                      <span className="font-semibold text-primary">
                        {formatAmount(call.totalAmount)}
                      </span>
                    </div>

                    {/* Date de création */}
                    <div className="text-xs text-muted-foreground">
                      Créé le {formatDate(call.createdAt)}
                    </div>
                  </div>

                  {/* Date d'envoi */}
                  {call.sentAt && (
                    <div className="mt-2 text-xs text-green-600 flex items-center">
                      <Mail className="w-3 h-3 mr-1" />
                      Envoyé le {formatDate(call.sentAt)} à {call.recipientEmail}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col space-y-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onView(call.id)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Détails
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDownloadExcel(call.id)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Excel
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDownloadPDF(call.id)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </Button>

                  {call.status !== 'SENT' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onSend(call.id)}
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Envoyer
                    </Button>
                  )}
                </div>
              </div>

              {/* Métadonnées supplémentaires */}
              {call.metadata?.byProduct && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Répartition par produit:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(call.metadata.byProduct).map(([product, stats]) => (
                      <div
                        key={product}
                        className="text-xs bg-gray-50 p-2 rounded"
                      >
                        <p className="font-medium truncate">{product}</p>
                        <p className="text-muted-foreground">
                          {stats.count} employé{stats.count > 1 ? 's' : ''}
                        </p>
                        <p className="font-semibold text-primary">
                          {formatAmount(stats.amount)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default CallHistory;
